import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { 
  ArrowRight, 
  ArrowLeft, 
  Bath, 
  ChefHat, 
  Grid3X3, 
  Sparkles, 
  Home, 
  HelpCircle,
  Upload,
  MapPin,
  Calendar,
  Check,
  X,
  CreditCard
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { toast } from "sonner";
import axios from "axios";
import { format } from "date-fns";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QuotePage = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  
  // Scroll to top when step changes - very aggressive implementation
  useEffect(() => {
    const scrollToTop = () => {
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    };
    
    // Immediate scroll
    scrollToTop();
    
    // Multiple attempts to ensure scroll sticks
    const timers = [
      setTimeout(scrollToTop, 0),
      setTimeout(scrollToTop, 10),
      setTimeout(scrollToTop, 50),
      setTimeout(scrollToTop, 100),
      setTimeout(scrollToTop, 200)
    ];
    
    // Also use requestAnimationFrame
    const raf = requestAnimationFrame(() => {
      scrollToTop();
      requestAnimationFrame(scrollToTop);
    });
    
    return () => {
      timers.forEach(t => clearTimeout(t));
      cancelAnimationFrame(raf);
    };
  }, [currentStep]);
  
  const [formData, setFormData] = useState({
    project_type: "",
    room_size: "",
    tile_type: "",
    project_stage: "",
    photos: [],
    postcode: "",
    name: "",
    phone: "",
    email: "",
    booking_date: null,
    booking_time: ""
  });
  const [estimate, setEstimate] = useState(null);
  const [postcodeValid, setPostcodeValid] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [quoteId, setQuoteId] = useState(null);

  const totalSteps = 10;
  const progress = (currentStep / totalSteps) * 100;

  // Fetch available slots when reaching booking step
  useEffect(() => {
    if (currentStep === 8) {
      fetchAvailableSlots();
    }
  }, [currentStep]);

  const fetchAvailableSlots = async () => {
    try {
      const response = await axios.get(`${API}/bookings/slots`);
      setAvailableSlots(response.data);
    } catch (error) {
      console.error("Error fetching slots:", error);
    }
  };

  const getEstimate = async () => {
    try {
      const response = await axios.post(`${API}/estimate`, {
        project_type: formData.project_type,
        room_size: formData.room_size,
        tile_type: formData.tile_type
      });
      setEstimate(response.data);
    } catch (error) {
      console.error("Error getting estimate:", error);
    }
  };

  const validatePostcode = async () => {
    try {
      const response = await axios.get(`${API}/validate-postcode/${formData.postcode}`);
      setPostcodeValid(response.data.in_service_area);
      return response.data.in_service_area;
    } catch (error) {
      console.error("Error validating postcode:", error);
      return false;
    }
  };

  const handleNext = async () => {
    // Validation for each step
    if (currentStep === 1 && !formData.project_type) {
      toast.error("Please select a project type");
      return;
    }
    if (currentStep === 2 && !formData.room_size) {
      toast.error("Please select a room size");
      return;
    }
    if (currentStep === 3 && !formData.tile_type) {
      toast.error("Please select a tile type");
      return;
    }
    if (currentStep === 4 && !formData.project_stage) {
      toast.error("Please select when you plan to start");
      return;
    }
    if (currentStep === 6 && !formData.postcode) {
      toast.error("Please enter your postcode");
      return;
    }
    if (currentStep === 6) {
      const valid = await validatePostcode();
      // Allow all postcodes to continue - just show info message for non-Manchester areas
    }
    if (currentStep === 6) {
      await getEstimate();
    }
    if (currentStep === 8 && (!formData.booking_date || !formData.booking_time)) {
      toast.error("Please select a date and time");
      return;
    }
    if (currentStep === 9) {
      if (!formData.name || !formData.phone || !formData.email) {
        toast.error("Please fill in all contact details");
        return;
      }
      await submitQuote();
      return;
    }

    // Scroll to top before changing step
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    
    setCurrentStep(prev => Math.min(prev + 1, totalSteps));
  };

  const handleBack = () => {
    // Scroll to top before changing step
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const submitQuote = async () => {
    setLoading(true);
    try {
      // Create quote
      const quoteResponse = await axios.post(`${API}/quotes`, {
        ...formData,
        estimated_min: estimate?.min_estimate || 0,
        estimated_max: estimate?.max_estimate || 0
      });
      
      const newQuoteId = quoteResponse.data.id;
      setQuoteId(newQuoteId);

      // Book the slot
      if (formData.booking_date && formData.booking_time) {
        await axios.post(`${API}/bookings`, {
          quote_id: newQuoteId,
          date: format(formData.booking_date, "yyyy-MM-dd"),
          time: formData.booking_time
        });
      }

      setCurrentStep(10);
      toast.success("Quote submitted successfully!");
    } catch (error) {
      console.error("Error submitting quote:", error);
      toast.error("Failed to submit quote. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length + formData.photos.length > 5) {
      toast.error("Maximum 5 photos allowed");
      return;
    }

    for (const file of files) {
      const formDataUpload = new FormData();
      formDataUpload.append("file", file);

      try {
        const response = await axios.post(`${API}/upload`, formDataUpload);
        setFormData(prev => ({
          ...prev,
          photos: [...prev.photos, response.data.data]
        }));
      } catch (error) {
        toast.error("Failed to upload photo");
      }
    }
  };

  const removePhoto = (index) => {
    setFormData(prev => ({
      ...prev,
      photos: prev.photos.filter((_, i) => i !== index)
    }));
  };

  const projectTypes = [
    { value: "bathroom", label: "Bathroom", icon: Bath },
    { value: "kitchen", label: "Kitchen", icon: ChefHat },
    { value: "floor", label: "Floor", icon: Grid3X3 },
    { value: "feature_wall", label: "Feature Wall / Splashback", icon: Sparkles },
    { value: "other", label: "Other", icon: HelpCircle }
  ];

  const roomSizes = [
    { value: "small", label: "Small", description: "Up to 5 sqm" },
    { value: "medium", label: "Medium", description: "5-15 sqm" },
    { value: "large", label: "Large", description: "15+ sqm" }
  ];

  const tileTypes = [
    { value: "ceramic", label: "Ceramic", description: "Classic and affordable" },
    { value: "porcelain", label: "Porcelain", description: "Durable and stylish" },
    { value: "natural_stone", label: "Natural Stone", description: "Marble, slate, etc." },
    { value: "large_format", label: "Large Format", description: "60x60cm or larger" },
    { value: "not_sure", label: "Not Sure", description: "Need advice" }
  ];

  const projectStages = [
    { value: "ready_now", label: "Ready Now", description: "Ready to start immediately" },
    { value: "1_3_months", label: "1-3 Months", description: "Planning to start soon" },
    { value: "just_planning", label: "Just Planning", description: "Gathering quotes" }
  ];

  const getAvailableTimes = () => {
    if (!formData.booking_date) return [];
    const dateStr = format(formData.booking_date, "yyyy-MM-dd");
    return availableSlots.filter(slot => slot.date === dateStr);
  };

  return (
    <div className="min-h-screen bg-white grid-pattern" data-testid="quote-page">
      {/* Progress Header */}
      <div className="sticky top-0 bg-white/95 backdrop-blur-md border-b border-gray-100 z-40">
        <div className="container-custom py-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-600">
              Step {currentStep} of {totalSteps}
            </span>
            <span className="text-sm font-medium text-primary">
              {Math.round(progress)}% Complete
            </span>
          </div>
          <Progress value={progress} className="h-2" data-testid="quote-progress" />
        </div>
      </div>

      {/* Main Content */}
      <div className="container-custom py-12">
        <div className="funnel-container">
          {/* Step 1: Project Type */}
          {currentStep === 1 && (
            <div className="funnel-step" data-testid="step-1">
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 text-center font-montserrat">
                What do you need tiled?
              </h1>
              <p className="text-gray-600 text-center mb-8">
                Select the type of project you're planning
              </p>
              <div className="grid sm:grid-cols-2 gap-4">
                {projectTypes.map((type) => (
                  <button
                    key={type.value}
                    onClick={() => setFormData({ ...formData, project_type: type.value })}
                    className={`funnel-card ${formData.project_type === type.value ? "selected" : ""}`}
                    data-testid={`project-type-${type.value}`}
                  >
                    <div className="funnel-card-icon">
                      <type.icon className="w-6 h-6" />
                    </div>
                    <span className="font-medium text-gray-900">{type.label}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 2: Room Size */}
          {currentStep === 2 && (
            <div className="funnel-step" data-testid="step-2">
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 text-center font-montserrat">
                Approximate size of the area
              </h1>
              <p className="text-gray-600 text-center mb-8">
                This helps us provide a more accurate estimate
              </p>
              <div className="space-y-4">
                {roomSizes.map((size) => (
                  <button
                    key={size.value}
                    onClick={() => setFormData({ ...formData, room_size: size.value })}
                    className={`funnel-card w-full ${formData.room_size === size.value ? "selected" : ""}`}
                    data-testid={`room-size-${size.value}`}
                  >
                    <div className="flex-1 text-left">
                      <span className="font-medium text-gray-900 block">{size.label}</span>
                      <span className="text-sm text-gray-500">{size.description}</span>
                    </div>
                    {formData.room_size === size.value && (
                      <Check className="w-5 h-5 text-primary" />
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 3: Tile Type */}
          {currentStep === 3 && (
            <div className="funnel-step" data-testid="step-3">
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 text-center font-montserrat">
                What type of tile are you using?
              </h1>
              <p className="text-gray-600 text-center mb-8">
                Different tile types affect pricing and installation
              </p>
              <div className="space-y-4">
                {tileTypes.map((type) => (
                  <button
                    key={type.value}
                    onClick={() => setFormData({ ...formData, tile_type: type.value })}
                    className={`funnel-card w-full ${formData.tile_type === type.value ? "selected" : ""}`}
                    data-testid={`tile-type-${type.value}`}
                  >
                    <div className="flex-1 text-left">
                      <span className="font-medium text-gray-900 block">{type.label}</span>
                      <span className="text-sm text-gray-500">{type.description}</span>
                    </div>
                    {formData.tile_type === type.value && (
                      <Check className="w-5 h-5 text-primary" />
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 4: Project Stage */}
          {currentStep === 4 && (
            <div className="funnel-step" data-testid="step-4">
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 text-center font-montserrat">
                When are you planning to start?
              </h1>
              <p className="text-gray-600 text-center mb-8">
                This helps us prioritise your quote
              </p>
              <div className="space-y-4">
                {projectStages.map((stage) => (
                  <button
                    key={stage.value}
                    onClick={() => setFormData({ ...formData, project_stage: stage.value })}
                    className={`funnel-card w-full ${formData.project_stage === stage.value ? "selected" : ""}`}
                    data-testid={`project-stage-${stage.value}`}
                  >
                    <div className="flex-1 text-left">
                      <span className="font-medium text-gray-900 block">{stage.label}</span>
                      <span className="text-sm text-gray-500">{stage.description}</span>
                    </div>
                    {formData.project_stage === stage.value && (
                      <Check className="w-5 h-5 text-primary" />
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 5: Photo Upload */}
          {currentStep === 5 && (
            <div className="funnel-step" data-testid="step-5">
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 text-center font-montserrat">
                Upload photos of the space
              </h1>
              <p className="text-gray-600 text-center mb-8">
                Optional: Photos help us provide a more accurate quote
              </p>
              
              <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-primary transition-colors">
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handlePhotoUpload}
                  className="hidden"
                  id="photo-upload"
                  data-testid="photo-upload-input"
                />
                <label htmlFor="photo-upload" className="cursor-pointer">
                  <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600 mb-2">Click to upload photos</p>
                  <p className="text-sm text-gray-500">Maximum 5 photos, 5MB each</p>
                </label>
              </div>

              {formData.photos.length > 0 && (
                <div className="grid grid-cols-3 gap-4 mt-6">
                  {formData.photos.map((photo, index) => (
                    <div key={index} className="relative aspect-square rounded-lg overflow-hidden">
                      <img src={photo} alt={`Upload ${index + 1}`} className="w-full h-full object-cover" />
                      <button
                        onClick={() => removePhoto(index)}
                        className="absolute top-2 right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center"
                        data-testid={`remove-photo-${index}`}
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Step 6: Postcode */}
          {currentStep === 6 && (
            <div className="funnel-step" data-testid="step-6">
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 text-center font-montserrat">
                Where is your project located?
              </h1>
              <p className="text-gray-600 text-center mb-8">
                Enter your postcode to check we cover your area
              </p>
              
              <div className="max-w-sm mx-auto">
                <div className="relative">
                  <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    type="text"
                    value={formData.postcode}
                    onChange={(e) => {
                      setFormData({ ...formData, postcode: e.target.value.toUpperCase() });
                      setPostcodeValid(null);
                    }}
                    placeholder="e.g. M1 1AA"
                    className="pl-12 h-14 text-lg"
                    data-testid="postcode-input"
                  />
                </div>
                {postcodeValid !== null && (
                  <p className={`mt-3 text-sm ${postcodeValid ? "text-green-600" : "text-orange-600"}`}>
                    {postcodeValid 
                      ? "✓ Great! We service your area." 
                      : "We are based in Greater Manchester but may travel further depending on the project."}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Step 7: Instant Estimate */}
          {currentStep === 7 && (
            <div className="funnel-step" data-testid="step-7">
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 text-center font-montserrat">
                Your Instant Estimate
              </h1>
              
              {estimate && (
                <div className="estimate-display mb-8">
                  <p className="text-white/80 mb-2">Estimated project range:</p>
                  <p className="estimate-price" data-testid="estimate-price">
                    £{estimate.min_estimate.toLocaleString()} – £{estimate.max_estimate.toLocaleString()}
                  </p>
                  <p className="text-white/70 text-sm mt-4 max-w-md mx-auto">
                    {estimate.disclaimer}
                  </p>
                </div>
              )}

              <div className="bg-gray-50 rounded-xl p-6 text-center">
                <h3 className="font-semibold text-gray-900 mb-2">Want a more accurate quote?</h3>
                <p className="text-gray-600 mb-4">
                  Book a free quote inspection and we'll provide a detailed, no-obligation quote.
                </p>
              </div>

              {/* Finance Option */}
              <div className="bg-primary/5 rounded-xl p-6 mt-6 border border-primary/20">
                <div className="flex items-center gap-3 mb-3">
                  <CreditCard className="w-6 h-6 text-primary" />
                  <h3 className="font-semibold text-gray-900">Spread the Cost</h3>
                </div>
                <p className="text-gray-600 mb-4">
                  Many customers choose to spread the cost of their project with monthly payments.
                </p>
                <Link to="/finance">
                  <Button variant="outline" className="border-primary text-primary hover:bg-primary/5" data-testid="quote-finance-link">
                    Check Finance Options
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>
          )}

          {/* Step 8: Booking Calendar */}
          {currentStep === 8 && (
            <div className="funnel-step" data-testid="step-8">
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 text-center font-montserrat">
                Book Your Quote Inspection
              </h1>
              <p className="text-gray-600 text-center mb-8">
                Select a convenient date and time
              </p>

              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <Label className="mb-2 block">Select a date</Label>
                  <div className="border rounded-xl p-4 bg-white">
                    <CalendarComponent
                      mode="single"
                      selected={formData.booking_date}
                      onSelect={(date) => setFormData({ ...formData, booking_date: date, booking_time: "" })}
                      disabled={(date) => {
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        return date < today || date.getDay() === 0;
                      }}
                      className="rounded-md"
                      data-testid="booking-calendar"
                    />
                  </div>
                </div>

                <div>
                  <Label className="mb-2 block">Select a time</Label>
                  <div className="grid grid-cols-2 gap-2">
                    {getAvailableTimes().map((slot) => (
                      <button
                        key={`${slot.date}-${slot.time}`}
                        onClick={() => slot.available && setFormData({ ...formData, booking_time: slot.time })}
                        className={`time-slot ${formData.booking_time === slot.time ? "selected" : ""} ${!slot.available ? "unavailable" : ""}`}
                        disabled={!slot.available}
                        data-testid={`time-slot-${slot.time}`}
                      >
                        {slot.time}
                      </button>
                    ))}
                  </div>
                  {!formData.booking_date && (
                    <p className="text-gray-500 text-sm mt-4">Please select a date first</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Step 9: Contact Details */}
          {currentStep === 9 && (
            <div className="funnel-step" data-testid="step-9">
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 text-center font-montserrat">
                Your Contact Details
              </h1>
              <p className="text-gray-600 text-center mb-8">
                We'll send your booking confirmation to these details
              </p>

              <div className="space-y-4 max-w-md mx-auto">
                <div>
                  <Label htmlFor="name">Full Name *</Label>
                  <Input
                    id="name"
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Your full name"
                    className="h-12 mt-1"
                    data-testid="contact-name"
                  />
                </div>
                <div>
                  <Label htmlFor="phone">Phone Number *</Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="Your phone number"
                    className="h-12 mt-1"
                    data-testid="contact-phone"
                  />
                </div>
                <div>
                  <Label htmlFor="email">Email Address *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="your@email.com"
                    className="h-12 mt-1"
                    data-testid="contact-email"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 10: Confirmation */}
          {currentStep === 10 && (
            <div className="funnel-step text-center" data-testid="step-10">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Check className="w-10 h-10 text-green-600" />
              </div>
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 font-montserrat">
                Your Quote Appointment is Booked!
              </h1>
              <p className="text-lg text-gray-600 mb-8">
                We've sent a confirmation to <strong>{formData.email}</strong>
              </p>

              <div className="bg-gray-50 rounded-xl p-6 max-w-md mx-auto mb-8">
                <h3 className="font-semibold text-gray-900 mb-4">Appointment Details</h3>
                <div className="space-y-2 text-left">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Date:</span>
                    <span className="font-medium">{formData.booking_date ? format(formData.booking_date, "EEEE, MMMM d, yyyy") : "N/A"}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Time:</span>
                    <span className="font-medium">{formData.booking_time || "N/A"}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Location:</span>
                    <span className="font-medium">{formData.postcode}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Project:</span>
                    <span className="font-medium capitalize">{formData.project_type?.replace("_", " ")}</span>
                  </div>
                </div>
              </div>

              <div className="bg-primary/5 rounded-xl p-6 max-w-md mx-auto mb-8">
                <p className="text-gray-700 mb-2">Questions? Call us on:</p>
                <a href="tel:+447547393455" className="text-2xl font-bold text-primary hover:text-primary/80">
                  07547 393 455
                </a>
              </div>

              <Button onClick={() => navigate("/")} size="lg" data-testid="back-to-home">
                Back to Home
              </Button>
            </div>
          )}

          {/* Navigation Buttons */}
          {currentStep < 10 && (
            <div className="flex items-center justify-between mt-12">
              <Button
                variant="ghost"
                onClick={handleBack}
                disabled={currentStep === 1}
                className="text-gray-600"
                data-testid="back-button"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <Button
                onClick={handleNext}
                disabled={loading}
                className="bg-primary hover:bg-primary/90 px-8"
                data-testid="next-button"
              >
                {loading ? "Submitting..." : currentStep === 9 ? "Submit" : "Continue"}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuotePage;
